<p-toast></p-toast>

<section class="workflow-builder">
    <header class="workflow-builder__header">
        <div>
            <h2>Workflow Engine</h2>
            <span>Build approval flows by connecting simple steps.</span>
        </div>
        <div class="workflow-builder__toolbar">
            <button pButton type="button" icon="pi pi-refresh" label="Reload" class="p-button-secondary" (click)="load()"></button>
            <button pButton type="button" icon="pi pi-plus" label="Resource" (click)="openResource()"></button>
            <button pButton type="button" icon="pi pi-sitemap" label="Workflow" (click)="openDefinition()"></button>
        </div>
    </header>

    <p-progressBar *ngIf="isLoading" mode="indeterminate" styleClass="mb-3"></p-progressBar>

    <div class="workflow-shell">
        <aside class="workflow-sidebar">
            <div class="workflow-sidebar__search">
                <i class="pi pi-search"></i>
                <input pInputText type="text" [(ngModel)]="workflowFilter" placeholder="Search workflows">
            </div>

            <div class="workflow-sidebar__group">
                <div class="workflow-sidebar__title">Workflows</div>
                @for (definition of filteredDefinitions; track definition.workflowDefinitionId) {
                    <button
                        type="button"
                        class="workflow-list-item"
                        [class.workflow-list-item--active]="selectedDefinition?.workflowDefinitionId === definition.workflowDefinitionId"
                        (click)="selectDefinition(definition)">
                        <span class="workflow-list-item__name">{{ definition.name }}</span>
                        <span class="workflow-list-item__meta">{{ definition.resourceKey }} · v{{ definition.version }}</span>
                    </button>
                }
                @if (!filteredDefinitions.length) {
                    <div class="workflow-empty">No workflows found.</div>
                }
            </div>

            <p-divider></p-divider>

            <div class="workflow-sidebar__group">
                <div class="workflow-sidebar__title">Resources</div>
                @for (resource of resourceTypes; track resource.resourceTypeId) {
                    <button type="button" class="resource-list-item" (click)="openResource(resource)">
                        <span>{{ resource.displayName }}</span>
                        <small>{{ resource.resourceKey }}</small>
                    </button>
                }
            </div>
        </aside>

        <main class="workflow-canvas-panel">
            @if (selectedDefinition) {
                <div class="workflow-canvas-panel__topbar">
                    <div>
                        <h3>{{ selectedDefinition.name }}</h3>
                        <span>{{ selectedDefinition.resourceKey }} starts at {{ selectedDefinition.initialStatusCode }}</span>
                    </div>
                    <div class="workflow-canvas-actions">
                        <button pButton type="button" icon="pi pi-plus-circle" label="Step" (click)="openNode()"></button>
                        <button pButton type="button" icon="pi pi-arrow-right-arrow-left" label="Action" class="p-button-secondary" (click)="openTransition()"></button>
                        <button pButton type="button" icon="pi pi-sitemap" label="Arrange" class="p-button-secondary" (click)="arrangeSteps()"></button>
                        <button pButton type="button" icon="pi pi-search-plus" class="p-button-secondary" pTooltip="Fit to screen" tooltipPosition="top" (click)="fitCanvas()"></button>
                        <button pButton type="button" icon="pi pi-refresh" class="p-button-secondary" pTooltip="Reset view" tooltipPosition="top" (click)="resetCanvas()"></button>
                    </div>
                </div>

                @if (validationMessages.length) {
                    <div class="workflow-validation-strip">
                        @for (message of validationMessages.slice(0, 3); track message.text) {
                            <span [class.workflow-validation-strip__warn]="message.severity === 'warn'">
                                <i class="pi" [class.pi-exclamation-triangle]="message.severity === 'warn'" [class.pi-times-circle]="message.severity === 'error'"></i>
                                {{ message.text }}
                            </span>
                        }
                    </div>
                }

                <div class="workflow-canvas">
                    <f-flow
                        fDraggable
                        [hCellSize]="20"
                        [vCellSize]="20"
                        [fCellSizeWhileDragging]="true"
                        (fMoveNodes)="onMoveNodes($event)"
                        (fCreateConnection)="onCreateConnection($event)"
                        (fReassignConnection)="onReassignConnection($event)"
                        (fSelectionChange)="onSelectionChange($event)">
                        <f-canvas fZoom>
                            <f-background>
                                <f-rect-pattern [hSize]="24" [vSize]="24" hColor="#e6edf5" vColor="#e6edf5"></f-rect-pattern>
                            </f-background>

                            @for (connection of graphConnections; track connection.id) {
                                <f-connection
                                    [fConnectionId]="connection.id"
                                    [fOutputId]="connection.outputId"
                                    [fInputId]="connection.inputId"
                                    [fReassignableStart]="true"
                                    fType="segment"
                                    (click)="openTransition(connection.transition)">
                                    <div
                                        fConnectionContent
                                        class="workflow-edge-label"
                                        [class.workflow-edge-label--rules]="connection.transition.requiresComment || connection.transition.requiresAttachment || connection.transition.allowedRoleId || connection.transition.allowedCustomerId || connection.transition.conditions?.length"
                                        (click)="openTransition(connection.transition); $event.stopPropagation()">
                                        <span>{{ connection.transition.label }}</span>
                                        @if (connection.transition.requiresComment) { <i class="pi pi-comment" pTooltip="Comment required"></i> }
                                        @if (connection.transition.requiresAttachment) { <i class="pi pi-paperclip" pTooltip="Attachment required"></i> }
                                        @if (connection.transition.allowedRole) { <i class="pi pi-lock" [pTooltip]="connection.transition.allowedRole"></i> }
                                        @if (connection.transition.allowedCustomerId) { <i class="pi pi-user" [pTooltip]="connection.transition.allowedCustomer?.customerName || ('Customer #' + connection.transition.allowedCustomerId)"></i> }
                                        @if (connection.transition.conditions?.length) { <i class="pi pi-filter" pTooltip="Rules"></i> }
                                    </div>
                                </f-connection>
                            }

                            @for (graphNode of graphNodes; track graphNode.id) {
                                <div
                                    fNode
                                    [fNodeId]="graphNode.id"
                                    [fNodePosition]="graphNode.position"
                                    class="workflow-step"
                                    [class.workflow-step--inactive]="graphNode.node.active === false"
                                    [class.workflow-step--selected]="selectedNode?.statusCode === graphNode.node.statusCode"
                                    (click)="openNode(graphNode.node)">
                                    <div class="workflow-step__connector workflow-step__connector--in" fNodeInput [fInputId]="graphNode.inputId" [fInputMultiple]="true"></div>
                                    <div class="workflow-step__connector workflow-step__connector--out" fNodeOutput [fOutputId]="graphNode.outputId" [fOutputMultiple]="true"></div>

                                    <div class="workflow-step__head" fDragHandle>
                                        <p-tag [value]="graphNode.node.active === false ? 'Inactive' : toTitle(graphNode.node.nodeType)" [severity]="tagSeverity(graphNode.node)"></p-tag>
                                        @if (selectedDefinition.initialStatusCode === graphNode.node.statusCode) {
                                            <span class="workflow-step__start">First</span>
                                        }
                                    </div>
                                    <div class="workflow-step__label">{{ graphNode.node.label }}</div>
                                    <div class="workflow-step__code">{{ graphNode.node.statusCode }}</div>
                                </div>
                            }

                            <f-selection-area></f-selection-area>
                        </f-canvas>
                        <f-minimap></f-minimap>
                    </f-flow>
                </div>

                <p-tabs [value]="activeReviewTab" (valueChange)="setActiveReviewTab($event)" styleClass="workflow-review-tabs">
                    <p-tablist>
                        <p-tab value="steps">All Steps</p-tab>
                        <p-tab value="actions">All Actions</p-tab>
                        <p-tab value="checks">Validation</p-tab>
                    </p-tablist>
                    <p-tabpanels>
                        <p-tabpanel value="steps">
                            <p-table [value]="selectedDefinition.nodes || []" styleClass="p-datatable-sm">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Step</th>
                                        <th>Status</th>
                                        <th>Type</th>
                                        <th>Final</th>
                                        <th>Active</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-node>
                                    <tr (click)="openNode(node)" [class.workflow-table-row--muted]="node.active === false">
                                        <td>{{ node.label }}</td>
                                        <td>{{ node.statusCode }}</td>
                                        <td>{{ toTitle(node.nodeType) }}</td>
                                        <td>{{ node.isFinal ? 'Yes' : 'No' }}</td>
                                        <td>{{ node.active ? 'Yes' : 'No' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </p-tabpanel>
                        <p-tabpanel value="actions">
                            <p-table [value]="selectedDefinition.transitions || []" styleClass="p-datatable-sm">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Action</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Rules</th>
                                        <th>Active</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-transition>
                                    <tr (click)="openTransition(transition)" [class.workflow-table-row--muted]="transition.active === false">
                                        <td>{{ transition.label }}</td>
                                        <td>{{ transition.fromStatusCode }}</td>
                                        <td>{{ transition.toStatusCode }}</td>
                                        <td>{{ transition.conditions?.length || 0 }}</td>
                                        <td>{{ transition.active ? 'Yes' : 'No' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </p-tabpanel>
                        <p-tabpanel value="checks">
                            @if (validationMessages.length) {
                                <div class="workflow-check-list">
                                    @for (message of validationMessages; track message.text) {
                                        <div class="workflow-check" [class.workflow-check--warn]="message.severity === 'warn'">
                                            <i class="pi" [class.pi-exclamation-triangle]="message.severity === 'warn'" [class.pi-times-circle]="message.severity === 'error'"></i>
                                            <span>{{ message.text }}</span>
                                        </div>
                                    }
                                </div>
                            } @else {
                                <div class="workflow-empty">No validation issues found.</div>
                            }
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            } @else {
                <div class="workflow-no-selection">
                    <i class="pi pi-sitemap"></i>
                    <h3>Create or select a workflow</h3>
                    <span>Choose a workflow on the left to start editing its approval steps.</span>
                </div>
            }
        </main>

        <aside class="workflow-inspector">
            @if (inspectorMode === 'workflow') {
                <div class="workflow-inspector__header">
                    <h3>Workflow Details</h3>
                    <span>Name, resource, and first step.</span>
                </div>
                <form [formGroup]="definitionForm" class="workflow-form">
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="workflowName" formControlName="name">
                                <label for="workflowName">Workflow name</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-key"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="workflowResourceKey" formControlName="resourceKey">
                                <label for="workflowResourceKey">Resource key</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber inputId="workflowVersion" formControlName="version" class="w-full"></p-inputNumber>
                                <label for="workflowVersion">Version</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="workflowInitialStatus" formControlName="initialStatusCode" [options]="nodeOptions" optionLabel="label" optionValue="value" placeholder="Choose first step"></p-select>
                                <label for="workflowInitialStatus">First step</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <label class="workflow-checkline"><p-checkbox formControlName="active" [binary]="true"></p-checkbox> Active workflow</label>
                    <button pButton type="button" icon="pi pi-save" label="Save Workflow" (click)="saveDefinition()"></button>
                </form>
            } @else if (inspectorMode === 'step') {
                <div class="workflow-inspector__header">
                    <h3>{{ selectedNode ? 'Step Details' : 'New Step' }}</h3>
                    <span>A step is a status users can understand.</span>
                </div>
                <form [formGroup]="nodeForm" class="workflow-form">
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-bookmark"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="nodeLabel" formControlName="label" placeholder="Ready for review">
                                <label for="nodeLabel">Step label</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-code"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="nodeStatusCode" formControlName="statusCode" placeholder="READY_FOR_REVIEW">
                                <label for="nodeStatusCode">Status code</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-shapes"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="nodeType" formControlName="nodeType" [options]="nodeTypes" optionLabel="label" optionValue="value"></p-select>
                                <label for="nodeType">Step type</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-sort-numeric-up"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber inputId="nodeSortOrder" formControlName="sortOrder" class="w-full"></p-inputNumber>
                                <label for="nodeSortOrder">Order</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form__grid">
                        <div class="workflow-form-field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-arrows-h"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-inputNumber inputId="nodePositionX" formControlName="positionX" class="w-full"></p-inputNumber>
                                    <label for="nodePositionX">Canvas X</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="workflow-form-field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-arrows-v"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-inputNumber inputId="nodePositionY" formControlName="positionY" class="w-full"></p-inputNumber>
                                    <label for="nodePositionY">Canvas Y</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                    <label class="workflow-checkline"><p-checkbox formControlName="isFinal" [binary]="true"></p-checkbox> This closes the workflow</label>
                    <label class="workflow-checkline"><p-checkbox formControlName="active" [binary]="true"></p-checkbox> Active step</label>
                    <div class="workflow-inspector__buttons">
                        <button pButton type="button" icon="pi pi-save" label="Save Step" (click)="saveNode()"></button>
                        <button pButton type="button" icon="pi pi-trash" label="Remove" class="p-button-danger p-button-outlined" [disabled]="!selectedNode?.nodeId" (click)="deleteNode()"></button>
                    </div>
                </form>
            } @else if (inspectorMode === 'action') {
                <div class="workflow-inspector__header">
                    <h3>{{ selectedTransition ? 'Action Details' : 'New Action' }}</h3>
                    <span>An action moves work from one step to another.</span>
                </div>
                <form [formGroup]="transitionForm" class="workflow-form">
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-megaphone"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="transitionLabel" formControlName="label" placeholder="Approve">
                                <label for="transitionLabel">Button label</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-code"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="transitionActionCode" formControlName="actionCode" placeholder="APPROVE">
                                <label for="transitionActionCode">Action code</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-arrow-right"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="transitionFromStatus" formControlName="fromStatusCode" [options]="nodeOptions" optionLabel="label" optionValue="value"></p-select>
                                <label for="transitionFromStatus">From step</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-arrow-left"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="transitionToStatus" formControlName="toStatusCode" [options]="nodeOptions" optionLabel="label" optionValue="value"></p-select>
                                <label for="transitionToStatus">To step</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>

                    <div class="workflow-rules-card">
                        <h4>Rules & restrictions</h4>
                        <label class="workflow-checkline"><p-checkbox formControlName="requiresComment" [binary]="true"></p-checkbox> Require a comment</label>
                        <label class="workflow-checkline"><p-checkbox formControlName="requiresAttachment" [binary]="true"></p-checkbox> Require an attachment</label>
                        <div class="workflow-form-field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select
                                        inputId="transitionAllowedRoleId"
                                        formControlName="allowedRoleId"
                                        [options]="roleOptions"
                                        optionLabel="roleName"
                                        optionValue="roleId"
                                        [showClear]="true"
                                        placeholder="Choose role">
                                    </p-select>
                                    <label for="transitionAllowedRoleId">Required role</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="workflow-form-field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-autoComplete
                                        inputId="transitionAllowedCustomer"
                                        formControlName="allowedCustomer"
                                        [suggestions]="filteredCustomerOptions"
                                        (completeMethod)="filterAllowedCustomers($event)"
                                        field="customerName"
                                        [dropdown]="true"
                                        [forceSelection]="true"
                                        appendTo="body"
                                        styleClass="w-full">
                                        <ng-template let-option pTemplate="item">
                                            <div class="workflow-option">
                                                <span class="workflow-option__title">{{ option.customerName }}</span>
                                                <small class="workflow-option__meta">{{ customerMeta(option) }}</small>
                                            </div>
                                        </ng-template>
                                        <ng-template let-option pTemplate="selectedItem">
                                            <div class="workflow-option workflow-option--selected">
                                                <span class="workflow-option__title">{{ option?.customerName }}</span>
                                            </div>
                                        </ng-template>
                                    </p-autoComplete>
                                    <label for="transitionAllowedCustomer">Specific customer override</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="workflow-field-help">Optional. Use this only when the action must be restricted to one internal customer.</small>
                        </div>
                    </div>

                    <label class="workflow-checkline"><p-checkbox formControlName="active" [binary]="true"></p-checkbox> Active action</label>
                    <div class="workflow-inspector__buttons">
                        <button pButton type="button" icon="pi pi-save" label="Save Action" (click)="saveTransition()"></button>
                        <button pButton type="button" icon="pi pi-trash" label="Remove" class="p-button-danger p-button-outlined" [disabled]="!selectedTransition?.transitionId" (click)="deleteTransition()"></button>
                    </div>
                </form>

                @if (selectedTransition) {
                    <div class="workflow-rule-list">
                        <div class="workflow-rule-list__head">
                            <h4>Conditions</h4>
                            <button pButton type="button" icon="pi pi-plus" label="Rule" size="small" (click)="openCondition(selectedTransition)"></button>
                        </div>
                        @for (condition of selectedTransition.conditions || []; track condition.conditionId) {
                            <button type="button" class="workflow-rule-item" [class.workflow-rule-item--muted]="condition.active === false" (click)="openCondition(selectedTransition, condition)">
                                <span>{{ toTitle(condition.conditionType) }}</span>
                                <small>{{ condition.errorMessage || 'No error message' }}</small>
                            </button>
                        }
                    </div>
                }
            } @else if (inspectorMode === 'condition') {
                <div class="workflow-inspector__header">
                    <h3>{{ selectedCondition ? 'Rule Details' : 'New Rule' }}</h3>
                    <span>Rules decide whether an action is available.</span>
                </div>
                <form [formGroup]="conditionForm" class="workflow-form">
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-filter"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="conditionType" formControlName="conditionType" [options]="conditionTypes" optionLabel="label" optionValue="value"></p-select>
                                <label for="conditionType">Rule type</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-code"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <textarea pInputTextarea inputId="conditionJson" formControlName="conditionJson" rows="8" placeholder='{"all":[{"field":"totalAmount","op":"GTE","value":10000}]}'></textarea>
                                <label for="conditionJson">Rule JSON</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-exclamation-circle"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText inputId="conditionErrorMessage" formControlName="errorMessage" placeholder="This action is not available.">
                                <label for="conditionErrorMessage">Error message</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="workflow-form-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-sort-numeric-up"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber inputId="conditionSortOrder" formControlName="sortOrder" class="w-full"></p-inputNumber>
                                <label for="conditionSortOrder">Order</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <label class="workflow-checkline"><p-checkbox formControlName="active" [binary]="true"></p-checkbox> Active rule</label>
                    <div class="workflow-inspector__buttons">
                        <button pButton type="button" icon="pi pi-save" label="Save Rule" (click)="saveCondition()"></button>
                        <button pButton type="button" icon="pi pi-trash" label="Remove" class="p-button-danger p-button-outlined" [disabled]="!selectedCondition?.conditionId" (click)="selectedCondition && deleteCondition(selectedCondition)"></button>
                    </div>
                </form>
            } @else {
                <div class="workflow-inspector__empty">
                    <i class="pi pi-mouse-pointer"></i>
                    <h3>Select something</h3>
                    <span>Click a workflow, step, or action to edit it here.</span>
                </div>
            }
        </aside>
    </div>
</section>

<p-dialog header="Resource Type" [(visible)]="showResourceDialog" [modal]="true" [style]="{ width: '34rem' }">
    <form [formGroup]="resourceForm" class="workflow-form">
        <div class="workflow-form-field">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-key"></i></p-inputGroupAddon>
                <p-iftaLabel>
                    <input pInputText inputId="resourceKey" formControlName="resourceKey" placeholder="SHIPMENT">
                    <label for="resourceKey">Resource key</label>
                </p-iftaLabel>
            </p-inputGroup>
        </div>
        <div class="workflow-form-field">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
                <p-iftaLabel>
                    <input pInputText inputId="resourceDisplayName" formControlName="displayName">
                    <label for="resourceDisplayName">Display name</label>
                </p-iftaLabel>
            </p-inputGroup>
        </div>
        <div class="workflow-form-field">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
                <p-iftaLabel>
                    <input pInputText inputId="resourceDetailRoute" formControlName="detailRouteTemplate">
                    <label for="resourceDetailRoute">Detail route template</label>
                </p-iftaLabel>
            </p-inputGroup>
        </div>
        <label class="flex align-items-center gap-2"><p-checkbox formControlName="active" [binary]="true"></p-checkbox> Active</label>
    </form>
    <ng-template pTemplate="footer">
        <button pButton type="button" label="Save" icon="pi pi-save" (click)="saveResource()"></button>
    </ng-template>
</p-dialog>
